import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { PLAN_OPTIONS, persistPendingPlan, planPricingFor } from '@/lib/plans'
import { ArrowRight } from 'lucide-react'

/**
 * Avant inscription : comparer Essentiel / Sérénité, enregistrer le choix en session, puis continuer vers /register.
 *
 * @param {{
 *   open: boolean
 *   onOpenChange: (open: boolean) => void
 *   analysisResult: Record<string, unknown> | null
 *   formatCurrency: (n: number | null | undefined) => string
 *   onContinueToRegister: () => void
 * }} props
 */
export default function PlanChoiceBeforeAuthDialog({
  open,
  onOpenChange,
  analysisResult,
  formatCurrency,
  onContinueToRegister,
}) {
  const [selectedPlan, setSelectedPlan] = useState('essentiel')

  useEffect(() => {
    if (open) setSelectedPlan('essentiel')
  }, [open])

  const handleContinue = () => {
    persistPendingPlan(selectedPlan)
    onOpenChange(false)
    onContinueToRegister()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg w-[calc(100vw-2rem)] rounded-2xl bg-[#1c1c1e] border-white/[0.08] text-[#f5f5f7] sm:max-w-lg"
        data-testid="plan-choice-before-auth-dialog"
      >
        <DialogHeader className="space-y-2 text-left">
          <DialogTitle className="text-xl font-bold tracking-tight font-heading text-[#f5f5f7]">
            Choisissez votre formule
          </DialogTitle>
          <DialogDescription className="text-sm text-[#f5f5f7]/55 leading-relaxed">
            Deux offres : frais fixes 20&nbsp;€ + une part de l&apos;économie annuelle estimée. Détails et garanties dans les{' '}
            <Link to="/cgv" className="text-green-400 underline-offset-2 hover:underline" onClick={() => onOpenChange(false)}>
              CGV
            </Link>
            . Vous pourrez modifier votre choix sur le tableau de bord avant paiement.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 mt-2">
          {PLAN_OPTIONS.map((opt) => {
            const row = planPricingFor(analysisResult, opt.id)
            const active = selectedPlan === opt.id
            return (
              <button
                key={opt.id}
                type="button"
                data-testid={`preauth-plan-${opt.id}-btn`}
                onClick={() => setSelectedPlan(opt.id)}
                className={`text-left rounded-xl border p-4 transition-[opacity,transform] duration-[180ms] ${
                  active
                    ? 'border-green-500/50 bg-green-500/10 ring-2 ring-green-500/30 scale-[1.01]'
                    : 'border-white/[0.08] bg-black hover:border-white/[0.14]'
                }`}
              >
                <p className="text-sm font-bold text-[#f5f5f7] font-heading">{opt.title}</p>
                <p className="text-xs text-[#f5f5f7]/45 mt-1">{opt.hint}</p>
                <p className="text-lg font-black text-green-400 font-heading mt-3">
                  {formatCurrency(row.amount)}
                </p>
              </button>
            )
          })}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col mt-2">
          <Button
            type="button"
            onClick={handleContinue}
            className="w-full h-12 btn-green rounded-xl text-base font-semibold gap-2"
            data-testid="preauth-plan-continue-btn"
          >
            Continuer vers l&apos;inscription
            <ArrowRight size={18} />
          </Button>
          <button
            type="button"
            className="text-xs text-[#f5f5f7]/40 hover:text-[#f5f5f7]/60 transition-opacity duration-[180ms]"
            onClick={() => onOpenChange(false)}
          >
            Annuler
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
